/**
 * Disposiciones de contacto por vivienda y tasa de respuesta AAPOR.
 *
 * §5.3.6: "Registro de disposiciones en CAPI para cada vivienda contactada
 * (entrevista completa, rechazo, nadie en casa, no elegible, vivienda
 * deshabitada) para calcular tasa de respuesta (AAPOR RR3)".
 *
 * §12 exige entregar la tasa de respuesta AAPOR POR ESTRATO como anexo.
 */
/**
 * Las cinco disposiciones que nombra §5.3.6, más las que el trabajo de campo
 * exige distinguir para que el denominador de la tasa de respuesta sea
 * correcto (parcial, no accesible y persona seleccionada ausente).
 */
export const DISPOSITIONS = [
    {
        code: 'entrevista_completa',
        label: 'Entrevista completa',
        aapor: 'interview-complete',
        final: true,
        allowsRevisit: false,
        description: 'La entrevista llegó al Bloque 12 con consentimiento y persona elegible.',
    },
    {
        code: 'entrevista_parcial',
        label: 'Entrevista parcial',
        aapor: 'interview-partial',
        final: true,
        allowsRevisit: false,
        description: 'La persona aceptó e inició, pero la entrevista se interrumpió antes de terminar. Se conserva lo capturado y se marca para decisión del validador.',
    },
    {
        code: 'rechazo',
        label: 'Rechazo',
        aapor: 'refusal',
        final: true,
        allowsRevisit: false,
        description: 'La persona elegible se negó a participar, o no otorgó el consentimiento de F6.',
    },
    {
        code: 'nadie_en_casa',
        label: 'Nadie en casa',
        aapor: 'non-contact',
        final: false,
        allowsRevisit: true,
        description: 'No se logró contacto con ningún residente. §5.3.4: hasta 3 intentos en distintos horarios antes de pasar a la siguiente vivienda del salto.',
    },
    {
        code: 'seleccionado_ausente',
        label: 'Persona seleccionada ausente',
        aapor: 'non-contact',
        final: false,
        allowsRevisit: true,
        description: 'Se contactó la vivienda y se identificó a la persona por el método de cumpleaños (F3), pero no estaba. §5.3.5: NUNCA sustituir por otra persona de la vivienda.',
    },
    {
        code: 'no_elegible',
        label: 'No elegible',
        aapor: 'not-eligible',
        final: true,
        allowsRevisit: false,
        description: 'No hay adultos con credencial para votar (F2 = 0), o la persona seleccionada no cumple el universo (F4 = no), o no es residente habitual (F1 = no).',
    },
    {
        code: 'vivienda_deshabitada',
        label: 'Vivienda deshabitada',
        aapor: 'not-eligible',
        final: true,
        allowsRevisit: false,
        description: 'La vivienda está deshabitada, en obra o no es vivienda particular.',
    },
    {
        code: 'no_accesible',
        label: 'No accesible',
        aapor: 'unknown-household',
        final: true,
        allowsRevisit: true,
        description: 'No se pudo determinar si la vivienda está habitada: acceso controlado, perro suelto, predio cerrado. Elegibilidad desconocida.',
    },
];
export const DISPOSITION_BY_CODE = Object.fromEntries(DISPOSITIONS.map((d) => [d.code, d]));
/** §5.3.4 — hasta 3 intentos en distintos horarios. */
export const MAX_REVISIT_ATTEMPTS = 3;
/**
 * Franjas horarias de las revisitas. §5.3.4 pide intentos en distintos
 * horarios (mañana, tarde, fin de semana) y §9.4 reprogramar a horario
 * vespertino y fin de semana a partir del día 6 si hay subcobertura de
 * jóvenes u hombres.
 */
export const VISIT_TIME_SLOTS = [
    { code: 'manana', label: 'Mañana (9:00–13:00)' },
    { code: 'tarde', label: 'Tarde (13:00–17:00)' },
    { code: 'vespertino', label: 'Vespertino (17:00–20:00)' },
    { code: 'fin_semana', label: 'Fin de semana' },
];
export function tallyDispositions(codes) {
    const tally = {
        interviewComplete: 0,
        interviewPartial: 0,
        refusal: 0,
        nonContact: 0,
        otherEligible: 0,
        unknownHousehold: 0,
        unknownOther: 0,
        notEligible: 0,
    };
    for (const code of codes) {
        const def = DISPOSITION_BY_CODE[code];
        if (!def)
            continue;
        switch (def.aapor) {
            case 'interview-complete':
                tally.interviewComplete++;
                break;
            case 'interview-partial':
                tally.interviewPartial++;
                break;
            case 'refusal':
                tally.refusal++;
                break;
            case 'non-contact':
                tally.nonContact++;
                break;
            case 'other-eligible':
                tally.otherEligible++;
                break;
            case 'unknown-household':
                tally.unknownHousehold++;
                break;
            case 'unknown-other':
                tally.unknownOther++;
                break;
            case 'not-eligible':
                tally.notEligible++;
                break;
        }
    }
    return tally;
}
/**
 * Estima `e`, la proporción de casos de elegibilidad desconocida que SON
 * elegibles, con el método proporcional que recomienda AAPOR: la proporción de
 * elegibles entre los casos cuya elegibilidad SÍ se pudo determinar.
 */
export function estimateEligibilityRate(tally) {
    const known = tally.interviewComplete +
        tally.interviewPartial +
        tally.refusal +
        tally.nonContact +
        tally.otherEligible;
    const denominator = known + tally.notEligible;
    if (denominator === 0)
        return 1;
    return known / denominator;
}
/**
 * AAPOR RR3 — Response Rate 3.
 *
 *            I
 *   ─────────────────────────────────────
 *   (I + P) + (R + NC + O) + e·(UH + UO)
 *
 * Las entrevistas parciales cuentan en el denominador pero NO en el numerador,
 * que es la diferencia entre RR3 y RR4.
 */
export function responseRateRR3(tally, eligibilityRate = estimateEligibilityRate(tally)) {
    const numerator = tally.interviewComplete;
    const denominator = tally.interviewComplete +
        tally.interviewPartial +
        tally.refusal +
        tally.nonContact +
        tally.otherEligible +
        eligibilityRate * (tally.unknownHousehold + tally.unknownOther);
    if (denominator === 0)
        return 0;
    return numerator / denominator;
}
/** Tasa de rechazo REF2: rechazos sobre el mismo denominador que RR3. */
export function refusalRateREF2(tally, eligibilityRate = estimateEligibilityRate(tally)) {
    const denominator = tally.interviewComplete +
        tally.interviewPartial +
        tally.refusal +
        tally.nonContact +
        tally.otherEligible +
        eligibilityRate * (tally.unknownHousehold + tally.unknownOther);
    if (denominator === 0)
        return 0;
    return tally.refusal / denominator;
}
/** Tasa de contacto CON2. */
export function contactRateCON2(tally, eligibilityRate = estimateEligibilityRate(tally)) {
    const contacted = tally.interviewComplete + tally.interviewPartial + tally.refusal + tally.otherEligible;
    const denominator = contacted + tally.nonContact + eligibilityRate * (tally.unknownHousehold + tally.unknownOther);
    if (denominator === 0)
        return 0;
    return contacted / denominator;
}
//# sourceMappingURL=dispositions.js.map